import type { ThemeId } from "../../api/generated/model";
import { CREATED_ID, type CreatedFamily, type SeededFamily, type ThemeFamily } from "./family";
import ayu from "./families/ayu.json";
import catppuccin from "./families/catppuccin.json";
import cyberdream from "./families/cyberdream.json";
import dracula from "./families/dracula.json";
import everforest from "./families/everforest.json";
import flexoki from "./families/flexoki.json";
import gruvbox from "./families/gruvbox.json";
import highContrast from "./families/high-contrast.json";
import horizon from "./families/horizon.json";
import nightOwl from "./families/night-owl.json";
import one from "./families/one.json";
import rosePine from "./families/rose-pine.json";
import solarized from "./families/solarized.json";
import tokyoNight from "./families/tokyo-night.json";
import { splitPage } from "./split";

/** Today's look, the default for everyone. */
export const CREATED: CreatedFamily = { id: CREATED_ID, name: "Created.ai" };

const seeded = (family: SeededFamily): SeededFamily => ({
  ...family,
  light: splitPage(family.light),
  dark: splitPage(family.dark),
});

/**
 * Every family by the API's `ThemeId`, in the picker's order: a theme the server knows and the web
 * does not, or the other way round, fails tsc here. A seeded family is a catalogue entry, its JSON
 * (`just themes-import`, spec web/themes-catalogue) and one line here.
 */
const BY_ID: Readonly<Record<ThemeId, ThemeFamily>> = {
  created: CREATED,
  "high-contrast": seeded(highContrast),
  "tokyo-night": seeded(tokyoNight),
  catppuccin: seeded(catppuccin),
  "rose-pine": seeded(rosePine),
  dracula: seeded(dracula),
  solarized: seeded(solarized),
  "night-owl": seeded(nightOwl),
  ayu: seeded(ayu),
  cyberdream: seeded(cyberdream),
  horizon: seeded(horizon),
  flexoki: seeded(flexoki),
  one: seeded(one),
  everforest: seeded(everforest),
  gruvbox: seeded(gruvbox),
};

/** Every family, in the picker's order. */
export const FAMILIES: readonly ThemeFamily[] = Object.values(BY_ID);

/** Whether `id` names a family: the API's `ThemeId`. */
export function isThemeId(id: string): id is ThemeId {
  return Object.hasOwn(BY_ID, id);
}

/** The family with `id`, or Created.ai for an id no family has (a family since removed). */
export function familyById(id: string): ThemeFamily {
  return isThemeId(id) ? BY_ID[id] : CREATED;
}
